<template>
  <!-- Loading spinner (separate component to avoid z-index issues) -->
  <div
    v-if="
      isRequestsLoading ||
      isLoading ||
      sendUserReminderEmailLoading ||
      sendInvitationLoading ||
      updateUserProgramStatusLoading
    "
    class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
  >
    <div
      class="animate-spin rounded-full h-32 w-32 border-t-4 border-b-4 border-primary-purple"
    ></div>
  </div>

  <div
    class="overflow-auto touch-pan-y max-h-[calc(100vh-200px)] border border-gray-300 rounded-md scroll-smooth"
  >
    <table class="min-w-full mb-24 border-separate border-spacing-0">
      <thead class="sticky top-0 bg-white z-10">
        <tr class="border-b border-gray-300">
          <th
            class="px-4 py-2 text-left text-gray-500 text-xs font-medium tracking-wider leading-4"
          >
            Name
          </th>
          <th
            class="px-4 py-2 text-left text-gray-500 text-xs font-medium tracking-wider leading-4"
          >
            Email
          </th>
          <th
            class="px-4 py-2 text-left text-gray-500 text-xs font-medium tracking-wider leading-4"
          >
            Total Thank You Payouts
          </th>
          <th
            class="px-4 py-2 text-left text-gray-500 text-xs font-medium tracking-wider leading-4"
          >
            Status
          </th>
          <th
            class="px-4 py-2 text-left text-gray-500 text-xs font-medium tracking-wider leading-4"
          >
            Last Activity
          </th>
          <th
            class="px-4 py-2 text-center text-gray-500 text-xs font-medium tracking-wider leading-4"
          >
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        <!-- Applied Requests -->
        <AppliedRequestRow
          v-for="(row, index) in sortedRows.requests"
          :key="'request-' + row.id"
          :row="row"
          :rowIndex="index"
          :convertToDate="convertToDate"
          @review="handleReviewEntry"
        />

        <!-- Active Experts -->
        <ExpertRowItem
          v-for="row in sortedRows.activeExperts"
          :key="'active-' + row.id"
          :row="row"
          :originalInvitationRows="originalInvitationRows"
          :shouldDisable="shouldDisable"
          :getClassForStatus="getClassForStatus"
          :expertStatusText="expertStatusText"
          :performActionText="performActionText"
          :performAction="performAction"
          :handleMakeInactive="handleMakeInactive"
          :lastActivityText="lastActivityText"
          :invitationStatus="invitationStatus"
          :invitationLastActivityText="invitationLastActivityText"
          :handleSendInvitation="handleSendInvitation"
          :getStatusTooltip="getStatusTooltip"
          :menuOpenId="menuOpenId"
          :setMenuOpenId="setMenuOpenId"
          :rowId="row.id"
        />

        <!-- Interested Experts -->
        <ExpertRowItem
          v-for="row in sortedRows.interestedExperts"
          :key="'interested-' + row.id"
          :row="row"
          :originalInvitationRows="originalInvitationRows"
          :shouldDisable="shouldDisable"
          :getClassForStatus="getClassForStatus"
          :expertStatusText="expertStatusText"
          :performActionText="performActionText"
          :performAction="performAction"
          :handleMakeInactive="handleMakeInactive"
          :lastActivityText="lastActivityText"
          :invitationStatus="invitationStatus"
          :invitationLastActivityText="invitationLastActivityText"
          :handleSendInvitation="handleSendInvitation"
          :getStatusTooltip="getStatusTooltip"
          :menuOpenId="menuOpenId"
          :setMenuOpenId="setMenuOpenId"
          :rowId="row.id"
        />

        <!-- Signup In Progress Experts -->
        <ExpertRowItem
          v-for="row in sortedRows.interestedSignupExperts"
          :key="'signup-' + row.id"
          :row="row"
          :originalInvitationRows="originalInvitationRows"
          :shouldDisable="shouldDisable"
          :getClassForStatus="getClassForStatus"
          :expertStatusText="expertStatusText"
          :performActionText="performActionText"
          :performAction="performAction"
          :handleMakeInactive="handleMakeInactive"
          :lastActivityText="lastActivityText"
          :invitationStatus="invitationStatus"
          :invitationLastActivityText="invitationLastActivityText"
          :handleSendInvitation="handleSendInvitation"
          :getStatusTooltip="getStatusTooltip"
          :menuOpenId="menuOpenId"
          :setMenuOpenId="setMenuOpenId"
          :rowId="row.id"
        />

        <!-- Invited Experts -->
        <ExpertRowItem
          v-for="row in sortedRows.invitedExperts"
          :key="'invited-' + row.id"
          :row="row"
          :originalInvitationRows="originalInvitationRows"
          :shouldDisable="shouldDisable"
          :getClassForStatus="getClassForStatus"
          :expertStatusText="expertStatusText"
          :performActionText="performActionText"
          :performAction="performAction"
          :handleMakeInactive="handleMakeInactive"
          :lastActivityText="lastActivityText"
          :invitationStatus="invitationStatus"
          :invitationLastActivityText="invitationLastActivityText"
          :handleSendInvitation="handleSendInvitation"
          :getStatusTooltip="getStatusTooltip"
          :menuOpenId="menuOpenId"
          :setMenuOpenId="setMenuOpenId"
          :rowId="row.id"
        />

        <!-- Invitations -->
        <ExpertRowItem
          v-for="row in sortedRows.invitations"
          :key="'invitation-' + row.id"
          :row="row"
          :isInvitation="true"
          :originalInvitationRows="originalInvitationRows"
          :shouldDisable="shouldDisable"
          :getClassForStatus="getClassForStatus"
          :expertStatusText="expertStatusText"
          :performActionText="performActionText"
          :performAction="performAction"
          :handleMakeInactive="handleMakeInactive"
          :lastActivityText="lastActivityText"
          :invitationStatus="invitationStatus"
          :invitationLastActivityText="invitationLastActivityText"
          :handleSendInvitation="handleSendInvitation"
          :getStatusTooltip="getStatusTooltip"
          :menuOpenId="menuOpenId"
          :setMenuOpenId="setMenuOpenId"
          :rowId="row.id"
        />

        <!-- Hidden Experts -->
        <ExpertRowItem
          v-for="row in sortedRows.hiddenExperts"
          :key="'hidden-' + row.id"
          :row="row"
          :originalInvitationRows="originalInvitationRows"
          :shouldDisable="shouldDisable"
          :getClassForStatus="getClassForStatus"
          :expertStatusText="expertStatusText"
          :performActionText="performActionText"
          :performAction="performAction"
          :handleMakeInactive="handleMakeInactive"
          :lastActivityText="lastActivityText"
          :invitationStatus="invitationStatus"
          :invitationLastActivityText="invitationLastActivityText"
          :handleSendInvitation="handleSendInvitation"
          :getStatusTooltip="getStatusTooltip"
          :menuOpenId="menuOpenId"
          :setMenuOpenId="setMenuOpenId"
          :rowId="row.id"
        />

        <!-- Other Experts -->
        <ExpertRowItem
          v-for="row in sortedRows.otherExperts"
          :key="'other-' + row.id"
          :row="row"
          :originalInvitationRows="originalInvitationRows"
          :shouldDisable="shouldDisable"
          :getClassForStatus="getClassForStatus"
          :expertStatusText="expertStatusText"
          :performActionText="performActionText"
          :performAction="performAction"
          :handleMakeInactive="handleMakeInactive"
          :lastActivityText="lastActivityText"
          :invitationStatus="invitationStatus"
          :invitationLastActivityText="invitationLastActivityText"
          :handleSendInvitation="handleSendInvitation"
          :getStatusTooltip="getStatusTooltip"
          :menuOpenId="menuOpenId"
          :setMenuOpenId="setMenuOpenId"
          :rowId="row.id"
        />
      </tbody>
    </table>
  </div>

  <ReviewEntryModal
    v-if="showReviewEntryModal"
    :isOpen="showReviewEntryModal"
    :onClose="() => (showReviewEntryModal = false)"
    :expert="requestsRows[selectedRequestIndex]?.user"
    :requestId="requestsRows[selectedRequestIndex]?.id"
    @refreshRequests="refreshExperts"
  />
</template>

<script lang="ts">
import { ApolloError, gql } from "@apollo/client/core";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { defineComponent, onMounted, ref } from "vue";
import {
  Expert,
  Invitation,
  JoinProgramRequest,
} from "@/types/expert-programs-types";
import { useUserStore } from "@/stores/user";
import { DOTS_VERTICAL_ICON } from "@/assets/svg/shared/svg-constants";
import { RequestRow } from "@/types/dashboard-types";
import ReviewEntryModal from "./ReviewEntryModal.vue";
import ExpertRowItem from "./ExpertRowItem.vue";
import AppliedRequestRow from "./AppliedRequestRow.vue";
import { useExpertsList } from "@/composables/useExpertsList";

const LIST_REQUESTS_QUERY = gql`
  query getCompanyJoinProgramRequest($status: Int!) {
    getCompanyJoinProgramRequest(status: $status) {
      id
      status
      createdAt
      user {
        id
        firstName
        lastName
        email
        uuid
        slug
        active
        onboardingStep
      }
    }
  }
`;

const LIST_INVITED_EXPERTS_QUERY = gql`
  query listProgramExperts($programId: ID!) {
    listProgramExperts(programId: $programId) {
      experts {
        id
        firstName
        lastName
        email
        slug
        title
        profile_picture
        linkedin_profile
        active
        onboardingStep
        updatedAt
        createdAt
        lastSyncCalendarReminderSentAt
        lastCompleteSignupReminderSentAt
        programsAddedAt
        programsCrmContact
        programStatus
        pastMeetingsCount(programId: $programId)
        futureMeetingsCount(programId: $programId)
        totalEarnings(programId: $programId)
        programProfileDetails(programId: $programId) {
          selectedExpertTags
        }
        programInvitation(programId: $programId) {
          id
          lastReminderSentAt
          createdAt
          updatedAt
        }
        joinedProgramViaInvite(programId: $programId)
        credits(programId: $programId)
        joinProgramRequest(programId: $programId) {
          id
          status
          updatedAt
        }
        calendarProviderAccount {
          accountExpiredAt
        }
      }
      invitations {
        id
        userEmail
        status
        lastReminderSentAt
        createdAt
      }
    }
  }
`;

const SEND_INVITATION_MUTATION = gql`
  mutation createInvitation($input: CreateInvitationInput!) {
    createInvitation(input: $input) {
      successMessage
      errors
    }
  }
`;

const SEND_USER_REMINDER_EMAIL_MUTATION = gql`
  mutation sendUserReminderEmail($input: SendUserReminderEmailInput!) {
    sendUserReminderEmail(input: $input) {
      success
    }
  }
`;

const UPDATE_USER_PROGRAM_STATUS_MUTATION = gql`
  mutation updateUserProgramStatus($input: UpdateUserProgramStatusInput!) {
    updateUserProgramStatus(input: $input) {
      success
      errors
    }
  }
`;

export default defineComponent({
  name: "ExpertsList",
  components: {
    ReviewEntryModal,
    ExpertRowItem,
    AppliedRequestRow,
  },
  props: {
    handleError: {
      type: Function,
      required: true,
    },
    openProfileUuid: {
      type: String,
      required: false,
    },
  },
  setup(props) {
    const expertsRows = ref<Expert[]>([]);
    const originalInvitationRows = ref<Invitation[]>([]);
    const filteredInvitationRows = ref<Invitation[]>([]);
    const errorMessage = ref("");
    const isLoading = ref(false);
    const isRequestsLoading = ref(false);
    const userStore = useUserStore();
    const requestsRows = ref<RequestRow[]>([]);
    const showReviewEntryModal = ref<boolean>(false);
    const selectedRequestIndex = ref<number>(0);
    const menuOpenId = ref<string | number | null>(null);
    const setMenuOpenId = (id: string | number | null) => {
      menuOpenId.value = id;
    };

    const {
      sortedRows,
      expertStatusText,
      updateSortedRows,
      lastActivityText,
      convertToDate,
      determineActionType,
      performActionText,
      getClassForStatus,
      getStatusTooltip,
      expertButtonText,
      invitationStatus,
    } = useExpertsList();

    const {
      mutate: sendInvitation,
      onDone: sendInvitationDone,
      onError: sendInvitationError,
      loading: sendInvitationLoading,
    } = useMutation(SEND_INVITATION_MUTATION);

    const {
      mutate: sendUserReminderEmail,
      onDone: sendUserReminderEmailDone,
      onError: sendUserReminderEmailError,
      loading: sendUserReminderEmailLoading,
    } = useMutation(SEND_USER_REMINDER_EMAIL_MUTATION);

    const {
      mutate: updateUserProgramStatus,
      onDone: updateUserProgramStatusDone,
      onError: updateUserProgramStatusError,
      loading: updateUserProgramStatusLoading,
    } = useMutation(UPDATE_USER_PROGRAM_STATUS_MUTATION);

    sendUserReminderEmailDone((response) => {
      if (response) {
        if (response.data?.sendUserReminderEmail?.success) {
          fetchExperts();
        } else {
          props.handleError("Failed to send reminder email");
        }
      }
    });

    sendUserReminderEmailError((error: ApolloError) => {
      if (error) {
        props.handleError(error.message);
      }
    });

    updateUserProgramStatusDone((response) => {
      if (response?.data?.updateUserProgramStatus?.success) {
        refreshExperts();
      } else {
        props.handleError("Failed to update user status.");
      }
    });

    updateUserProgramStatusError((error: ApolloError) => {
      if (error) {
        props.handleError(error.message);
      }
    });

    const fetchExperts = () => {
      isLoading.value = true;
      const { onResult: onResultExperts, onError: onErrorExperts } = useQuery(
        LIST_INVITED_EXPERTS_QUERY,
        {
          programId: userStore.selectedProgram?.id,
        },
        { fetchPolicy: "network-only" }
      );

      onResultExperts((result) => {
        if (result) {
          if (result.data) {
            expertsRows.value = result.data.listProgramExperts.experts.filter(
              (expert: Expert) =>
                !(
                  expert.joinProgramRequest?.[0]?.status === "pending" &&
                  expert.onboardingStep === "completed"
                )
            );
            originalInvitationRows.value =
              result.data.listProgramExperts.invitations;
            filteredInvitationRows.value =
              result.data.listProgramExperts.invitations.filter(
                (invitation: Invitation) =>
                  !result.data.listProgramExperts.experts.some(
                    (expert: Expert) => expert.email === invitation.userEmail
                  )
              );

            updateSortedRows(
              expertsRows.value,
              requestsRows.value,
              filteredInvitationRows.value,
              originalInvitationRows.value
            );
          }
          isLoading.value = false;
        }
      });

      onErrorExperts((error) => {
        if (error) {
          props.handleError(error.message);
          isLoading.value = false;
        }
      });
    };

    const fetchRequests = () => {
      isRequestsLoading.value = true;
      const { onResult: onResultRequests, onError: onErrorRequests } = useQuery(
        LIST_REQUESTS_QUERY,
        { status: 0 },
        { fetchPolicy: "network-only" }
      );

      onResultRequests((result) => {
        if (result) {
          if (result.data) {
            requestsRows.value =
              result.data.getCompanyJoinProgramRequest.filter(
                (request: RequestRow) =>
                  request.user.onboardingStep === "completed"
              );
            checkAutoProfileSelection();

            updateSortedRows(
              expertsRows.value,
              requestsRows.value,
              filteredInvitationRows.value,
              originalInvitationRows.value
            );
          }
          isRequestsLoading.value = false;
        }
      });

      onErrorRequests((error) => {
        if (error) {
          props.handleError(error.message);
          isRequestsLoading.value = false;
        }
      });
    };

    const checkAutoProfileSelection = () => {
      if (props.openProfileUuid) {
        const profile = requestsRows.value.find(
          (row) => row.user.uuid === props.openProfileUuid
        );
        if (profile) {
          handleReviewEntry(requestsRows.value.indexOf(profile));
        }
      }
    };

    const handleReviewEntry = (index: number) => {
      selectedRequestIndex.value = index;
      showReviewEntryModal.value = true;
    };

    const handleSendInvitation = (email: string) => {
      sendInvitation({
        input: {
          inviteParams: {
            emails: [email],
            programId: userStore.selectedProgram?.id,
          },
          inviteType: "experts",
          directSignup: true,
        },
      });

      sendInvitationDone((response) => {
        if (response) {
          if (response.errors) {
            props.handleError(response.errors.join(", "));
          } else if (response.data.createInvitation.errors.length > 0) {
            props.handleError(response.data.createInvitation.errors.join(", "));
          } else {
            fetchExperts();
          }
        }
      });

      sendInvitationError((error: ApolloError) => {
        if (error) {
          props.handleError(error.message);
        }
      });
    };

    const shouldDisable = (joinProgramrequestStatus: string) => {
      return joinProgramrequestStatus === "rejected";
    };

    const performAction = (expertRow: Expert) => {
      const actionType = determineActionType(
        expertRow,
        originalInvitationRows.value
      );
      if (
        actionType === "send_calendar_disconnected_reminder" ||
        actionType === "send_complete_signup_reminder"
      ) {
        sendUserReminderEmail({
          input: {
            userEmail: expertRow.email,
            reminderEmailType: actionType,
            programId: userStore.selectedProgram?.id,
          },
        });
      } else if (actionType === "send_invitation_email") {
        handleSendInvitation(expertRow.email);
      } else if (actionType === "make_expert_inactive") {
        updateUserProgramStatus({
          input: {
            userId: expertRow.id,
            programId: userStore.selectedProgram?.id,
            active: false,
          },
        });
      } else if (actionType === "make_expert_active") {
        updateUserProgramStatus({
          input: {
            userId: expertRow.id,
            programId: userStore.selectedProgram?.id,
            active: true,
          },
        });
      }
    };

    const handleMakeInactive = (expertRow: Expert) => {
      updateUserProgramStatus({
        input: {
          userId: expertRow.id,
          programId: userStore.selectedProgram?.id,
          active: false,
        },
      });
    };

    const invitationLastActivityText = (invitation: Invitation) => {
      if (invitation.lastReminderSentAt) {
        return "Reminder sent " + convertToDate(invitation.lastReminderSentAt);
      } else {
        return "Invited on " + convertToDate(invitation.createdAt);
      }
    };

    const refreshExperts = () => {
      fetchExperts();
      fetchRequests();
    };

    onMounted(() => {
      refreshExperts();
    });

    return {
      expertsRows,
      filteredInvitationRows,
      errorMessage,
      sendInvitationLoading,
      expertButtonText,
      handleSendInvitation,
      invitationStatus,
      shouldDisable,
      getClassForStatus,
      DOTS_VERTICAL_ICON,
      refreshExperts,
      isLoading,
      showReviewEntryModal,
      selectedRequestIndex,
      requestsRows,
      fetchRequests,
      handleReviewEntry,
      convertToDate,
      expertStatusText,
      isRequestsLoading,
      performActionText,
      performAction,
      handleMakeInactive,
      sendUserReminderEmailLoading,
      updateUserProgramStatusLoading,
      lastActivityText,
      invitationLastActivityText,
      sortedRows,
      getStatusTooltip,
      originalInvitationRows,
      menuOpenId,
      setMenuOpenId,
    };
  },
});
</script>
